import React, { useEffect, useState } from "react";
import { X, BellOff, Share, RefreshCw } from "lucide-react";
import { pushDiagnostics, isIOS, isStandalonePWA } from "@/lib/push";
import api from "@/lib/api";

/**
 * PushReinstallNudge — surface a soft prompt when we detect a mismatch:
 *   • Device is iOS.
 *   • App is installed as PWA (running standalone).
 *   • User has notify_push = true in their preferences.
 *   • Yet the browser has NO active push subscription (typical: iOS silently
 *     expired the subscription after a SW / VAPID update, or the user deleted
 *     + reinstalled without re-enabling push).
 *
 * This is the "silent iOS push failure" pattern. Rather than making the user
 * hunt through the notification prefs, we proactively guide them.
 *
 * Behaviour:
 *   - Runs only once per session (localStorage key with 7-day cooldown).
 *   - Bottom-sheet style card, non-blocking, dismissible.
 *   - Skips entirely on non-iOS or in Safari tabs.
 */
const STORAGE_KEY = "adn.pushReinstallNudge.lastShown";
const COOLDOWN_MS = 7 * 24 * 3600 * 1000;  // 7 days

export default function PushReinstallNudge() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!isIOS() || !isStandalonePWA()) return;
        // Cooldown check
        const last = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
        if (last && Date.now() - last < COOLDOWN_MS) return;

        // Only proceed if user is logged in — check via /auth/me
        let user = null;
        try {
          const { data } = await api.get("/auth/me");
          user = data;
        } catch (_e) { return; }
        if (!user) return;

        // Fetch prefs — only nudge if user WANTS push
        let prefs = null;
        try {
          const { data } = await api.get("/consumer/notification-preferences");
          prefs = data;
        } catch (_e) { return; }
        if (!prefs?.notify_push) return;

        // Diagnostics — subscription must be MISSING for the nudge to make sense
        const diag = await pushDiagnostics();
        if (!diag.is_ios || !diag.is_standalone_pwa) return;
        if (diag.notification_permission === "denied") return; // separate flow
        if (diag.has_push_subscription) return; // already good

        // All conditions met — show the nudge after a short delay
        if (cancelled) return;
        setTimeout(() => !cancelled && setVisible(true), 3000);
      } catch (_e) {
        // Fail silently — this is a UX polish, not critical
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-[calc(env(safe-area-inset-bottom)+96px)] md:bottom-6 left-4 right-4 md:left-6 md:right-auto md:max-w-sm z-[70]"
      data-testid="push-reinstall-nudge"
    >
      <div className="adn-card p-4 border border-primary/40 shadow-xl bg-[#0a1226]/95 backdrop-blur relative">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="push-reinstall-nudge-close"
          className="absolute top-2 right-2 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <BellOff size={20} className="text-yellow-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Notifications aren&apos;t reaching your iPhone</div>
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              iOS silently expired your push subscription. Reinstall the app to fix it in 30 seconds — you&apos;ll get winner alerts, streak reminders, and prize drops again.
            </p>
            <ol className="text-[11px] text-white/60 mt-2 space-y-1 leading-relaxed list-decimal ml-4">
              <li>Long-press the FanFest icon on your Home Screen → <i>Remove App</i></li>
              <li>Open Safari and go to this site again</li>
              <li>Tap <Share size={10} className="inline align-baseline" /> Share → <i>Add to Home Screen</i></li>
              <li>Open FanFest from the Home Screen &amp; enable push again</li>
            </ol>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={dismiss}
                data-testid="push-reinstall-nudge-cta"
                className="text-xs font-semibold px-3 py-1.5 rounded bg-primary text-black hover:brightness-110 inline-flex items-center gap-1.5"
              >
                <RefreshCw size={12} /> Got it
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="text-[11px] text-white/40 hover:text-white/70"
              >
                Remind me later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
